/**
 * ServiceBox Local Proxy client.
 * Talks to the Python proxy running on a garage PC.
 * Uses the proxy_url from org settings, falls back to localhost:3847.
 */

const DEFAULT_PROXY_BASE = "http://localhost:3847";

let _proxyBase = DEFAULT_PROXY_BASE;

export function setProxyBase(url: string) {
  // Strip trailing slash
  _proxyBase = url.replace(/\/+$/, "");
}

export function getProxyBase(): string {
  return _proxyBase;
}

export interface ServiceBoxCredentials {
  username: string;
  password: string;
  site_code?: string;
  operator_email?: string;
}

export interface AgendaOption {
  id: string;
  name: string;
}

export interface AgendaOptions {
  receptionnaires: AgendaOption[];
  equipes: AgendaOption[];
}

export interface CreateRdvParams extends ServiceBoxCredentials {
  date: string; // YYYYMMDD
  heure: string; // HHMM
  restitution_date?: string; // YYYYMMDD — restitution day, may differ from reception (multi-day RDV)
  restitution_heure: string;
  receptionnaire_id: string;
  restitution_receptionnaire_id?: string; // ServiceBox allows a different person for restitution; defaults to receptionnaire_id
  equipe_id: string;
  rdv_id?: string; // when set, UPDATE this existing ServiceBox RDV instead of creating a new one
  dossier_id?: string; // existing ServiceBox dossier id, for the update flow
  nom: string;
  prenom: string;
  tel_mobile: string;
  email?: string;
  civilite_id?: string;
  adresse_rue?: string;
  adresse_code_postal?: string;
  adresse_ville?: string;
  marque?: string;
  ldp_libelle?: string;
  marque_libelle?: string;
  vin?: string;
  immatriculation?: string;
  kilometrage?: string;
  mec_day?: string;
  mec_month?: string;
  mec_year?: string;
  travaux?: { nom: string; duree: string; type?: string; forfait_code?: string }[]; // type: ServiceBox intervention type ("5" = Forfait Local, empty = Standard); forfait_code → LDT "code"
  travail_nom?: string;
  travail_duree?: string;
  pdv_id?: string;
  code_pays?: string;
}

export interface StepResult {
  name: string;
  status: "ok" | "error" | "skipped";
  detail: string;
}

export interface CreateRdvResult {
  success: boolean;
  or_number?: string;
  dossier_id?: string;
  rdv_id?: string;
  client_id?: string;
  error?: string;
  steps: StepResult[];
}

export interface TestConnectionResult {
  connected: boolean;
  session_ok: boolean;
  servicebox_reachable: boolean;
  detail: string;
}

export interface HealthResult {
  status: string;
  version: string;
  sessions: number;
}

export interface LogEntry {
  ts: string;
  level: string;
  step: string;
  message: string;
}

export class ProxyBusyError extends Error {
  constructor() {
    super("busy");
    this.name = "ProxyBusyError";
  }
}

async function proxyFetch<T>(path: string, body?: object): Promise<T> {
  const opts: RequestInit = body
    ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
    : { method: "GET" };
  const resp = await fetch(`${_proxyBase}${path}`, opts);
  if (resp.status === 423) throw new ProxyBusyError();
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Proxy error ${resp.status}: ${text}`);
  }
  return resp.json();
}

/**
 * Call proxyFetch with automatic retry on 423 (busy).
 * Calls onBusy() each time a retry happens so the UI can react.
 */
export async function proxyFetchWithRetry<T>(
  path: string,
  body?: object,
  onBusy?: () => void,
  maxRetries = 30,
  delayMs = 2000,
): Promise<T> {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await proxyFetch<T>(path, body);
    } catch (e) {
      if (e instanceof ProxyBusyError && i < maxRetries - 1) {
        onBusy?.();
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }
      throw e;
    }
  }
  throw new Error("Proxy occupe trop longtemps, veuillez reessayer");
}

export const serviceboxProxy = {
  async checkHealth(): Promise<HealthResult | null> {
    try {
      const resp = await fetch(`${_proxyBase}/health`, { signal: AbortSignal.timeout(2000) });
      if (!resp.ok) return null;
      return resp.json();
    } catch {
      return null;
    }
  },

  async testConnection(creds: ServiceBoxCredentials): Promise<TestConnectionResult> {
    return proxyFetch("/test-connection", creds);
  },

  async getLogs(limit = 50): Promise<LogEntry[]> {
    const data = await proxyFetch<{ logs: LogEntry[] }>(`/logs?limit=${limit}`);
    return data.logs;
  },

  async getOptions(creds: ServiceBoxCredentials): Promise<AgendaOptions> {
    return proxyFetch("/options", creds);
  },

  async createRdv(params: CreateRdvParams, onBusy?: () => void): Promise<CreateRdvResult> {
    return proxyFetchWithRetry("/create-rdv", params, onBusy);
  },

  async resetSession(creds: ServiceBoxCredentials): Promise<void> {
    await proxyFetch("/reset-session", creds);
  },

  async deleteRdv(creds: ServiceBoxCredentials, rdvId: string): Promise<{ success: boolean; error?: string }> {
    return proxyFetch("/delete-rdv", { ...creds, rdv_id: rdvId });
  },

  async searchClient(creds: ServiceBoxCredentials, phone: string, nom: string): Promise<{ found: boolean; dms_id?: string; nom?: string; prenom?: string; detail: string }> {
    return proxyFetch("/search-client", { ...creds, phone, nom });
  },

  async debugAuth(creds: ServiceBoxCredentials): Promise<{ results: Array<{ test: string; result: string; detail: string }> }> {
    return proxyFetch("/debug-auth", creds);
  },

  async fetchEstimation(creds: ServiceBoxCredentials, dossierId: string): Promise<{ success: boolean; html?: string; error?: string; needs_forfait_choice?: boolean; forfait_choices?: ForfaitChoice[] }> {
    return proxyFetch("/fetch-estimation", { ...creds, dossier_id: dossierId });
  },

  /** Submit the picked forfait variant(s), then ServiceBox re-computes the devis. */
  async chooseForfait(creds: ServiceBoxCredentials, dossierId: string, selections: { field: string; value: string }[]): Promise<{ success: boolean; html?: string; error?: string; needs_forfait_choice?: boolean; forfait_choices?: ForfaitChoice[] }> {
    return proxyFetch("/choose-forfait", { ...creds, dossier_id: dossierId, selections });
  },
};

export interface ForfaitChoice {
  field: string;
  code: string;
  designation: string;
  prix_ht: string;
  prix_ttc: string;
  selected: boolean;
}
